import type { HTMLComponent } from "@commercekit/react";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import { useMemo } from "react";

// Akeneo category codes arrive prefixed; the backend matches the prefix case-insensitively
// and without the trailing underscore, so accept both shapes here.
const CATEGORY_CODE_PREFIX = /^WebB2C_z_?/i;

const WvProductProsAndCons: HTMLComponent<{
  productName?: string;
  advantages?: string[];
  disadvantages?: string[];
  categories?: Array<{
    id: string;
    name: string;
  }>;
}> = ({ productName, advantages, disadvantages, categories }) => {
  const { $pt } = usePrismicLabels();

  // Opt-in is data-driven: a category shows this text only when its label has content.
  // Crumbs without the category prefix (the product itself) are skipped.
  const categoryDescription = useMemo(() => {
    const crumbs = categories ?? [];

    // walk leaf -> root so the most specific category wins
    for (let i = crumbs.length - 1; i >= 0; i--) {
      const { id, name } = crumbs[i];
      const prefix = CATEGORY_CODE_PREFIX.exec(id);
      if (!prefix) continue;

      // Prismic API ids are lowercase, the category code's casing is not guaranteed
      const description = $pt(id.slice(prefix[0].length).toLowerCase(), {}, true)?.trim();
      if (description && name?.trim()) return { name, description };
    }

    return null;
  }, [categories, $pt]);

  return (
    <section className="mb-3 pdp__pros-cons">
      {/* wrapper holds the underline/spacing so heading and intro can be separate elements */}
      <div className="pb-3 mb-3 heading sans-serif md strong underlined left multiline">
        <h2>
          {categoryDescription ? (
            categoryDescription.name
          ) : (
            <>
              {$pt("product_label_why_choose")} {productName}?
            </>
          )}
        </h2>

        {categoryDescription ? (
          <p className="text-base mt-2">{categoryDescription.description}</p>
        ) : null}
      </div>

      <ul className="pdp__pros-cons-plus">
        {advantages?.slice(0, 4)?.map((advantage, index) => (
          <li className="py-1" data-icon-before="" key={advantage + index}>
            {advantage}
          </li>
        ))}
      </ul>

      <ul className="pdp__pros-cons-min">
        {disadvantages?.slice(0, 2)?.map((disadvantage, index) => (
          <li className="py-1" data-icon-before="" key={disadvantage + index}>
            {disadvantage}
          </li>
        ))}
      </ul>
    </section>
  );
};
export default WvProductProsAndCons;
